import { fetchAuthSession } from "aws-amplify/auth";
import { client as privateClient, publicClient } from "queryClient";
import {
  Product,
  ProductName,
  ProductSelectionSet,
  ProductShortened,
} from "types";
import utils from "utils";

export const getProductListByExpansion = async (
  expansionID: string,
  nextTokenParam: string | null = null,
  prevProductList: Product[] = [],
): Promise<Product[]> => {
  const { data, nextToken } =
    await privateClient.models.Product.productsByExpansion(
      {
        expansionID,
      },
      {
        limit: 1000,
        nextToken: nextTokenParam,
      },
    );

  const productList = [...prevProductList, ...data];

  return nextToken
    ? getProductListByExpansion(expansionID, nextToken, productList)
    : productList;
};

export const getProductListByExpansionWithNoDeStock = async (
  isAuth: boolean,
  expansionID: string,
  nextTokenParam: string | null = null,
  prevProductList: ProductShortened[] = [],
): Promise<ProductShortened[]> => {
  const client = isAuth ? privateClient : publicClient;

  const { data, nextToken } =
    await client.models.Product.productsByExpansionByDeStock(
      {
        expansionID,
        deStock: {
          eq: 0,
        },
      },
      {
        limit: 1000,
        nextToken: nextTokenParam,
        selectionSet: ProductSelectionSet,
      },
    );

  const productList = [...prevProductList, ...data];

  return nextToken
    ? getProductListByExpansionWithNoDeStock(
        isAuth,
        expansionID,
        nextToken,
        productList,
      )
    : productList;
};

export const getProductListByExpansionWithNoEnStock = async (
  isAuth: boolean,
  expansionID: string,
  nextTokenParam: string | null = null,
  prevProductList: ProductShortened[] = [],
): Promise<ProductShortened[]> => {
  const client = isAuth ? privateClient : publicClient;

  const { data, nextToken } =
    await client.models.Product.productsByExpansionByEnStock(
      {
        expansionID,
        enStock: {
          eq: 0,
        },
      },
      {
        limit: 1000,
        nextToken: nextTokenParam,
        selectionSet: ProductSelectionSet,
      },
    );

  const productList = [...prevProductList, ...data];

  return nextToken
    ? getProductListByExpansionWithNoEnStock(
        isAuth,
        expansionID,
        nextToken,
        productList,
      )
    : productList;
};

export const getProductListByExpansionShortened = async (
  isAuth: boolean,
  expansionID: string,
  nextTokenParam: string | null = null,
  prevProductList: ProductShortened[] = [],
): Promise<ProductShortened[]> => {
  const client = isAuth ? privateClient : publicClient;

  const { data, nextToken } = await client.models.Product.productsByExpansion(
    {
      expansionID,
    },
    {
      limit: 1000,
      nextToken: nextTokenParam,
      selectionSet: ProductSelectionSet,
    },
  );

  const productList = [...prevProductList, ...data];

  return nextToken
    ? getProductListByExpansionShortened(
        isAuth,
        expansionID,
        nextToken,
        productList,
      )
    : productList;
};

export const getSmallProductListByExpansion = async (
  expansionID: string,
): Promise<Product[]> => {
  const { data } = await privateClient.models.Product.productsByExpansion(
    {
      expansionID,
    },
    {
      limit: 10,
    },
  );

  return data;
};

export const getAllProductList = async (
  isProductActive: boolean | null = true,
  nextTokenParam: string | null = null,
  prevProductList: Product[] = [],
): Promise<Product[]> => {
  const { data, nextToken } = await privateClient.models.Product.list({
    limit: 15000,
    nextToken: nextTokenParam,
    filter:
      isProductActive === null
        ? undefined
        : {
            isProductActive: {
              eq: isProductActive,
            },
          },
  });

  const productList = [...prevProductList, ...data];

  return nextToken
    ? getAllProductList(isProductActive, nextToken, productList)
    : productList;
};

export const getProduct = async (id: string): Promise<Product | null> => {
  const { data } = await privateClient.models.Product.get({
    id,
  });

  return data;
};

export const getProductShortened = async (
  id: string,
): Promise<ProductShortened | null> => {
  const { data } = await publicClient.models.Product.get(
    {
      id,
    },
    {
      selectionSet: ProductSelectionSet,
    },
  );

  return data;
};

export const getProductListByIDs = async (
  idList: string[],
): Promise<Product[]> => {
  if (idList.length === 0) {
    return [];
  }

  const productList: Product[] = [];

  const idListChunks = utils.general.sliceIntoChunks(idList, 50);

  for (const idListChunk of idListChunks) {
    const getProductPromises = idListChunk.map((id) => getProduct(id));

    const productListChunk = await Promise.all(getProductPromises);

    const definedProductListChunk =
      utils.graphql.getDefinedItems(productListChunk);

    productList.push(...definedProductListChunk);
  }

  return productList;
};

export const getProductListByIDsShortened = async (
  idList: string[],
): Promise<ProductShortened[]> => {
  if (idList.length === 0) {
    return [];
  }

  const productList: ProductShortened[] = [];

  const idListChunks = utils.general.sliceIntoChunks(idList, 50);

  for (const idListChunk of idListChunks) {
    const getProductPromises = idListChunk.map((id) => getProductShortened(id));

    const productListChunk = await Promise.all(getProductPromises);

    const definedProductListChunk =
      utils.graphql.getDefinedItems(productListChunk);

    productList.push(...definedProductListChunk);
  }

  return productList;
};

export const getProductNamesList = async (
  isProductActive: boolean | null = null,
): Promise<ProductName[]> => {
  const productNames: ProductName[] = [];
  let token = null;

  do {
    const { data } = await privateClient.queries.listProductNames({
      isProductActive,
      limit: 30000,
      token,
    });

    if (data) {
      productNames.push(...data.productList);
      token = data.nextToken;
    }
  } while (token);

  return productNames;
};

export const getProductNamesListPublic = async (
  isProductActive: boolean | null = null,
): Promise<ProductName[]> => {
  const productNames: ProductName[] = [];
  let token = null;

  const authSession = await fetchAuthSession();

  if (authSession.tokens) {
    // User is authenticated, use private client
    const productNameList = await getProductNamesList(isProductActive);
    return productNameList;
  }

  do {
    console.log("Fetching public product names with token:", {
      authSession,
      token,
    });
    const { data } = await publicClient.queries.listProductNames({
      // isProductActive,
      limit: 30000,
      token,
    });

    if (data) {
      productNames.push(...data.productList);
      token = data.nextToken;
    }
  } while (token);

  return productNames;
};

/**
 * Berechnet den Preis einer einzelnen Karte synchron neu (2-5 s).
 *
 * Fachliche Fehlschläge ("keine Angebote am Markt") kommen als Ergebnis mit
 * success: false zurück, nicht als Exception - nur so kann die Oberfläche den
 * Grund anzeigen statt eines generischen Fehlers. Geworfen wird ausschließlich
 * bei Transportfehlern.
 */
export const recalculateProductPrice = async (productID: string) => {
  const { data, errors } =
    await privateClient.mutations.recalculateProductPrice({
      productID,
    });

  if (errors?.length) {
    throw errors[0];
  }

  return data;
};
